import type { EventCapturer } from '#src/analytics/analytics_event.ts';
import { useWishlistAction } from '#src/pages/group/own_wishlist_card/use_wishlist_action.ts';
import type {
  WishlistFormErrors,
  WishlistFormValues,
} from '#src/pages/group/own_wishlist_card/wishlist_item_form.ts';
import {
  buildCreateWishlistAction,
  buildSetWishlistAction,
  clearFieldError,
  createWishlistFormValues,
  WISHLIST_NOTE_LIMIT,
  WISHLIST_TITLE_LIMIT,
  WISHLIST_URL_LIMIT,
} from '#src/pages/group/own_wishlist_card/wishlist_item_form.ts';
import {
  WishlistItemCreateEvent,
  WishlistItemDeleteEvent,
  WishlistItemUpdateEvent,
} from '#src/wishlists/analytics/wishlist_analytics_events.ts';
import type { WishlistClient } from '#src/wishlists/http/wishlist_client.ts';
import type {
  PerformWishlistActionResponse,
  WishlistActionRequest,
  WishlistItem,
} from '@packages/contracts/wishlist';
import { WishlistAction } from '@packages/contracts/wishlist';
import { exists } from '@packages/primitives/exists';
import { UnreachableError } from '@packages/primitives/unreachable_error';
import { Alert } from '@packages/ui/base/alert/alert';
import { Button } from '@packages/ui/base/button/button';
import { AdaptiveDialog } from '@packages/ui/base/dialog/adaptive_dialog';
import { Input } from '@packages/ui/base/input/input';
import { Label } from '@packages/ui/base/input/label';
import { Textarea } from '@packages/ui/base/input/textarea';
import { PlusIcon } from '@phosphor-icons/react';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import { useState } from 'react';

export interface WishlistItemDialogProps {
  captureEvent: EventCapturer;
  wishlistClient: WishlistClient;
  currentItemCount: number;
  open: boolean;
  item: WishlistItem | null;
  groupId: string;
  participantId: string;
  onOpenChange: (open: boolean) => void;
}

export const WishlistItemDialog = ({
  wishlistClient,
  captureEvent,
  currentItemCount,
  open,
  item,
  groupId,
  participantId,
  onOpenChange,
}: WishlistItemDialogProps) => {
  const wishlistAction = useWishlistAction(wishlistClient, { groupId, participantId });
  const resetWishlistAction = wishlistAction.reset;

  const handleOpenChange = (nextOpen: boolean) => {
    if (wishlistAction.isPending) {
      return;
    }

    if (!nextOpen) {
      resetWishlistAction();
    }

    onOpenChange(nextOpen);
  };

  return (
    <AdaptiveDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AdaptiveDialog.Content>
        <WishlistItemDialogForm
          captureEvent={captureEvent}
          currentItemCount={currentItemCount}
          item={item}
          wishlistAction={wishlistAction}
          onClose={() => handleOpenChange(false)}
        />
      </AdaptiveDialog.Content>
    </AdaptiveDialog.Root>
  );
};

const PLACEHOLDERS = [
  'Cashmere socks',
  'A cozy blanket',
  'Noise-canceling headphones',
  'A good cookbook',
  'Concert tickets',
  'Scented candles',
  'A streaming subscription',
  'Chocolate gift box',
];
const CREATE_DIALOG_TITLE = 'Add an item';
const EDIT_DIALOG_TITLE = 'Edit an item';
const CREATE_SUBMIT_LABEL = 'Add item';
const EDIT_SUBMIT_LABEL = 'Save changes';

type WishlistActionState = ReturnType<typeof useWishlistAction>;

interface WishlistItemDialogFormProps {
  captureEvent: EventCapturer;
  currentItemCount: number;
  item: WishlistItem | null;
  wishlistAction: WishlistActionState;
  onClose: () => void;
}

interface UseWishlistItemFormArgs {
  item: WishlistItem | null;
  resetWishlistAction: () => void;
}

interface WishlistDialogFooterActionsProps {
  isPending: boolean;
  onClose: () => void;
  onDelete: () => Promise<void>;
  showDeleteAction: boolean;
  submitLabel: string;
}

interface FormFieldProps {
  children: ReactNode;
  error?: string;
  htmlFor: string;
  label: string;
  limit: number;
  currentLength?: number;
  optional?: boolean;
}

function useWishlistItemForm({ item, resetWishlistAction }: UseWishlistItemFormArgs) {
  const [formValues, setFormValues] = useState<WishlistFormValues>(() =>
    createWishlistFormValues(item),
  );
  const [fieldErrors, setFieldErrors] = useState<WishlistFormErrors>({});
  const [isNoteVisible, setIsNoteVisible] = useState(() => exists(item?.note));
  const [titlePlaceholder] = useState(
    () => PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)],
  );

  const showNoteField = isNoteVisible;
  const showAddNoteButton = !showNoteField;

  const handleFieldChange =
    (field: keyof WishlistFormValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      resetWishlistAction();
      setFieldErrors(current => clearFieldError(current, field));
      setFormValues(current => ({
        ...current,
        [field]: event.target.value,
      }));
    };

  return {
    fieldErrors,
    formValues,
    handleFieldChange,
    setFieldErrors,
    showAddNoteButton,
    showNoteField,
    showNoteFieldInput: () => setIsNoteVisible(true),
    titlePlaceholder,
  };
}

const WishlistItemDialogForm = ({
  captureEvent,
  currentItemCount,
  item,
  onClose,
  wishlistAction,
}: WishlistItemDialogFormProps) => {
  const resetWishlistAction = wishlistAction.reset;
  const {
    fieldErrors,
    formValues,
    handleFieldChange,
    setFieldErrors,
    showAddNoteButton,
    showNoteField,
    showNoteFieldInput,
    titlePlaceholder,
  } = useWishlistItemForm({ item, resetWishlistAction });

  const isEditing = exists(item);
  const isPending = wishlistAction.isPending;
  const showDeleteAction = isEditing;
  const showError = exists(wishlistAction.displayErrorMessage);

  const dialogTitle = isEditing ? EDIT_DIALOG_TITLE : CREATE_DIALOG_TITLE;
  const submitLabel = isEditing ? EDIT_SUBMIT_LABEL : CREATE_SUBMIT_LABEL;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationResult = !exists(item)
      ? buildCreateWishlistAction(formValues)
      : buildSetWishlistAction(item.id, formValues);

    if (!validationResult.success) {
      setFieldErrors(validationResult.fieldErrors);
      return;
    }

    setFieldErrors({});

    try {
      const result = await wishlistAction.mutateAsync(validationResult.action);
      trackWishlistSubmitAnalytics({
        action: validationResult.action,
        captureEvent,
        currentItemCount,
        result,
      });

      if (result.success) {
        onClose();
      }
    } catch {
      trackWishlistSubmitAnalytics({
        action: validationResult.action,
        captureEvent,
        currentItemCount,
        result: null,
      });
      // The hook exposes the unexpected error state.
    }
  };

  const handleDelete = async () => {
    if (!exists(item)) {
      return;
    }

    resetWishlistAction();

    try {
      const result = await wishlistAction.mutateAsync({
        action: WishlistAction.DELETE,
        id: item.id,
      });
      trackWishlistDeleteAnalytics({
        captureEvent,
        currentItemCount,
        result,
      });

      if (result.success) {
        onClose();
      }
    } catch {
      trackWishlistDeleteAnalytics({
        captureEvent,
        currentItemCount,
        result: null,
      });
      // The hook exposes the unexpected error state.
    }
  };

  return (
    <div className="flex max-h-[calc(100dvh-4rem)] flex-col overflow-hidden">
      {/* ---- Header ---- */}
      <AdaptiveDialog.Header className="px-6 pt-5 pb-4">
        <AdaptiveDialog.Title>{dialogTitle}</AdaptiveDialog.Title>
        <AdaptiveDialog.Description>
          Your match sees saved changes right away.
        </AdaptiveDialog.Description>
      </AdaptiveDialog.Header>

      <form
        noValidate
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {/* ---- Fields ---- */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-2">
          <FormField
            htmlFor="wishlist-title"
            label="What do you wish for?"
            limit={WISHLIST_TITLE_LIMIT}
            currentLength={formValues.title.length}
            error={fieldErrors.title}
          >
            <Input
              id="wishlist-title"
              type="text"
              value={formValues.title}
              onChange={handleFieldChange('title')}
              maxLength={WISHLIST_TITLE_LIMIT}
              disabled={isPending}
              aria-invalid={exists(fieldErrors.title)}
              placeholder={titlePlaceholder}
            />
          </FormField>

          <div className="flex flex-col gap-2">
            <FormField
              htmlFor="wishlist-url"
              label="Link"
              optional
              limit={WISHLIST_URL_LIMIT}
              currentLength={formValues.url.length}
              error={fieldErrors.url}
            >
              <Input
                id="wishlist-url"
                type="text"
                inputMode="url"
                value={formValues.url}
                onChange={handleFieldChange('url')}
                maxLength={WISHLIST_URL_LIMIT}
                disabled={isPending}
                aria-invalid={exists(fieldErrors.url)}
                placeholder="https://example.com/item"
              />
            </FormField>

            {showNoteField && (
              <div className="mt-2 mb-4">
                <FormField
                  htmlFor="wishlist-note"
                  label="Note"
                  optional
                  limit={WISHLIST_NOTE_LIMIT}
                  currentLength={formValues.note.length}
                  error={fieldErrors.note}
                >
                  <Textarea
                    id="wishlist-note"
                    value={formValues.note}
                    onChange={handleFieldChange('note')}
                    maxLength={WISHLIST_NOTE_LIMIT}
                    disabled={isPending}
                    aria-invalid={exists(fieldErrors.note)}
                    placeholder="Color, size, or any other details"
                    className="min-h-[80px]"
                  />
                </FormField>
              </div>
            )}

            {showAddNoteButton && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="mb-2 -ml-2 self-start"
                onClick={showNoteFieldInput}
              >
                <PlusIcon className="mr-1 size-3.5" weight="bold" />
                <span>Add a note</span>
              </Button>
            )}
          </div>

          {showError && <Alert variant="error">{wishlistAction.displayErrorMessage}</Alert>}
        </div>

        {/* ---- Footer ---- */}
        <WishlistDialogFooterActions
          isPending={isPending}
          onClose={onClose}
          onDelete={handleDelete}
          showDeleteAction={showDeleteAction}
          submitLabel={submitLabel}
        />
      </form>
    </div>
  );
};

const WishlistDialogFooterActions = ({
  isPending,
  onClose,
  onDelete,
  showDeleteAction,
  submitLabel,
}: WishlistDialogFooterActionsProps) => {
  return (
    <div className="px-6 pb-4">
      <div
        data-testid="wishlist-dialog-footer-actions"
        className="flex flex-col items-center gap-3 sm:flex-row sm:items-center"
      >
        <div
          data-testid="wishlist-dialog-primary-actions"
          className="order-1 grid w-full max-w-sm grid-cols-2 gap-3 sm:order-2 sm:ml-auto sm:flex sm:w-auto sm:max-w-none sm:justify-end"
        >
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isPending}
          >
            <span>Cancel</span>
          </Button>
          <Button type="submit" size="sm" loading={isPending} disabled={isPending}>
            <span>{submitLabel}</span>
          </Button>
        </div>

        {showDeleteAction && (
          <Button
            type="button"
            variant="secondary"
            tone="destructive"
            size="sm"
            onClick={onDelete}
            disabled={isPending}
            className="order-2 w-full max-w-sm sm:order-1 sm:w-auto"
          >
            <span>Delete item</span>
          </Button>
        )}
      </div>
    </div>
  );
};

function trackWishlistSubmitAnalytics({
  action,
  captureEvent,
  currentItemCount,
  result,
}: {
  action: Extract<
    WishlistActionRequest,
    { action: typeof WishlistAction.CREATE | typeof WishlistAction.SET }
  >;
  captureEvent: EventCapturer;
  currentItemCount: number;
  result: PerformWishlistActionResponse | null;
}) {
  switch (action.action) {
    case WishlistAction.CREATE: {
      const isSuccess = result?.success === true && result.action === WishlistAction.CREATE;
      const retailer = isSuccess ? result.item.retailer : null;

      captureEvent(WishlistItemCreateEvent, {
        isSuccess,
        hasUrl: exists(action.url),
        hasNote: exists(action.note),
        retailerName: isSuccess ? (retailer?.name ?? null) : null,
        retailerId: isSuccess ? (retailer?.id ?? null) : null,
        itemCount: isSuccess ? currentItemCount + 1 : null,
      });
      return;
    }

    case WishlistAction.SET: {
      const isSuccess = result?.success === true && result.action === WishlistAction.SET;
      const retailer = isSuccess ? result.item.retailer : null;

      captureEvent(WishlistItemUpdateEvent, {
        isSuccess,
        hasUrl: exists(action.url),
        hasNote: exists(action.note),
        retailerName: isSuccess ? (retailer?.name ?? null) : null,
        retailerId: isSuccess ? (retailer?.id ?? null) : null,
      });
      return;
    }

    default:
      throw new UnreachableError(action);
  }
}

function trackWishlistDeleteAnalytics({
  captureEvent,
  currentItemCount,
  result,
}: {
  captureEvent: EventCapturer;
  currentItemCount: number;
  result: PerformWishlistActionResponse | null;
}) {
  const isSuccess = result?.success === true && result.action === WishlistAction.DELETE;

  captureEvent(WishlistItemDeleteEvent, {
    isSuccess,
    itemCount: isSuccess ? Math.max(0, currentItemCount - 1) : null,
  });
}

const FormField = ({
  children,
  error,
  htmlFor,
  label,
  limit,
  currentLength = 0,
  optional = false,
}: FormFieldProps) => {
  const showLimit = currentLength >= limit * 0.7;
  const limitToneClassName = currentLength >= limit ? 'font-medium text-red-500' : 'text-zinc-400';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={htmlFor}>
          {label} {optional && <span className="font-normal text-zinc-400">(optional)</span>}
        </Label>
        {showLimit && (
          <span className={`text-xs ${limitToneClassName}`}>
            {currentLength} / {limit}
          </span>
        )}
      </div>

      {children}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
